import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/joy';
import { Typography as MuiTypography } from '@mui/material';

const tableCellStyle = { padding: '2px 6px', borderBottom: '1px solid #f3f4f6' };
const thStyle = { textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid #e5e7eb' };

export const OTP_PARAMS = [
  { var: '1', desc: 'OTP code (one-time password)', example: '"847291"' },
];

export const INVOICE_PARAMS = [
  { var: '1', desc: 'Customer ID', example: '"42"' },
  { var: '2', desc: 'Customer first_name_ar', example: '"أحمد"' },
  { var: '3', desc: 'Customer last_name_ar', example: '"علي"' },
  { var: '4', desc: 'Customer first_name', example: '"Ahmed"' },
  { var: '5', desc: 'Customer last_name', example: '"Ali"' },
  { var: '6', desc: 'Customer username', example: '"ahmed_user"' },
  { var: '7', desc: 'Customer id_number', example: '"12345678"' },
  { var: '8', desc: 'Customer opt_status', example: '"opted_in"' },
  { var: '9', desc: 'Customer phone_number', example: '"96891234567"' },
  { var: '10', desc: 'Invoice payment_status', example: '"unpaid"' },
  { var: '11', desc: 'Invoice payment_link', example: '"https://..."' },
  { var: '12', desc: 'Invoice payment_link_created_at', example: '"2024-01-15 10:30:00"' },
  { var: '13', desc: 'Invoice number (invoice id)', example: '"42"' },
  // Variable 14 is not sent for invoices
  { var: '15', desc: 'Invoice note', example: '"January order"' },
  { var: '16', desc: 'Shipment IDs (comma-separated)', example: '"101, 102"' },
  { var: '17', desc: 'Shipment shipper_references (comma-separated)', example: '"REF-1, REF-2"' },
  { var: '18', desc: 'Shipment tracking_numbers (comma-separated)', example: '"TRK1, TRK2"' },
  { var: '19', desc: 'Shipment packages_quantity (comma-separated)', example: '"2, 1"' },
  { var: '20', desc: 'Shipment prices (comma-separated)', example: '"10.5, 8.2"' },
  { var: '21', desc: 'Shipment location names (comma-separated)', example: '"Muscat, Sohar"' },
  { var: '22', desc: 'Shipment statuses (comma-separated)', example: '"shipped, shipped"' },
  { var: '23', desc: 'Shipment invoice quantities with units (comma-separated; from billing_metric)', example: '"12.500 Kg, 2.000 CBM"' },
];

export const SHIPMENT_STATUS_PARAMS = [
  { var: '1', desc: 'Owner first_name_ar', example: '"أحمد"' },
  { var: '2', desc: 'Owner first_name', example: '"Ahmed"' },
  { var: '3', desc: 'Shipment ID', example: '"12345"' },
  { var: '4', desc: 'packages_quantity — count of active (non-deleted) pieces; falls back to stored packages_quantity if no piece rows yet', example: '"2"' },
  { var: '5', desc: 'tracking_number', example: '"TRK123"' },
  { var: '6', desc: 'shipper_reference', example: '"REF-001"' },
  { var: '7', desc: 'volumetric_weight', example: '"5.500"' },
  { var: '8', desc: 'price per volumetric_weight (price / volumetric_weight)', example: '"1.200"' },
  { var: '9', desc: 'price', example: '"6.600"' },
  { var: '10', desc: 'Owner username', example: '"ahmed_user"' },
  { var: '11', desc: 'Owner last_name', example: '"Ali"' },
  { var: '12', desc: 'Owner last_name_ar', example: '"علي"' },
  { var: '13', desc: 'Owner id_number', example: '"12345678"' },
  { var: '14', desc: 'Owner opt_status', example: '"opted_in"' },
  { var: '15', desc: 'weight', example: '"3.000"' },
  { var: '16', desc: 'width_cm', example: '"10.000"' },
  { var: '17', desc: 'length_cm', example: '"20.000"' },
  { var: '18', desc: 'height_cm', example: '"15.000"' },
  { var: '19', desc: 'charge', example: '"5.000"' },
  { var: '20', desc: 'auto_calculating_price', example: '"True"' },
  { var: '21', desc: 'location name', example: '"Muscat"' },
  { var: '22', desc: 'note', example: '"Handle with care"' },
  { var: '23', desc: 'status', example: '"shipped"' },
  { var: '24', desc: 'deleted', example: '"False"' },
  { var: '25', desc: 'payment_status', example: '"unpaid"' },
  { var: '26', desc: 'invoiced', example: '"False"' },
  { var: '27', desc: 'Event type English (ShipmentTrackingEventType.name)', example: '"Shipped"' },
  { var: '28', desc: 'Shipment status in Arabic (the status just saved). Falls back to the event type Arabic name only when the status is not a known shipping status.', example: '"تم التسليم"' },
  { var: '29', desc: 'Owner phone_number', example: '"96891234567"' },
];

export const SHIPMENT_DELETED_PARAMS = [
  { var: '1', desc: 'Owner: first_name_ar + ( phone_number )', example: '"أحمد (96891234567)"' },
  { var: '2', desc: 'Owner first_name', example: '"Ahmed"' },
  { var: '3', desc: 'Shipment ID', example: '"12345"' },
  { var: '4', desc: 'packages_quantity', example: '"2"' },
  { var: '5', desc: 'tracking_number', example: '"TRK123"' },
  { var: '6', desc: 'shipper_reference (or "N/A")', example: '"REF-001"' },
  { var: '7', desc: 'volumetric_weight', example: '"5.500"' },
  { var: '8', desc: 'price per volumetric_weight', example: '"1.200"' },
  { var: '9', desc: 'price', example: '"6.600"' },
  { var: '10', desc: 'Owner username', example: '"ahmed_user"' },
  { var: '11', desc: 'Owner last_name', example: '"Ali"' },
  { var: '12', desc: 'Owner last_name_ar', example: '"علي"' },
  { var: '13', desc: 'Owner id_number', example: '"12345678"' },
  { var: '14', desc: 'Owner opt_status', example: '"opted_in"' },
  { var: '15', desc: 'weight', example: '"3.000"' },
  { var: '16', desc: 'width_cm', example: '"10.000"' },
  { var: '17', desc: 'length_cm', example: '"20.000"' },
  { var: '18', desc: 'height_cm', example: '"15.000"' },
  { var: '19', desc: 'charge', example: '"5.000"' },
  { var: '20', desc: 'auto_calculating_price', example: '"True"' },
  { var: '21', desc: 'location name', example: '"Muscat" or ""' },
  { var: '22', desc: 'note', example: '"Handle with care"' },
  { var: '23', desc: 'status', example: '"shipped"' },
  { var: '24', desc: 'deleted', example: '"True"' },
  { var: '25', desc: 'payment_status', example: '"unpaid"' },
  { var: '26', desc: 'invoiced', example: '"False"' },
  { var: '27', desc: 'event_type display', example: '"Shipped"' },
  { var: '28', desc: 'items_description', example: '"Electronics"' },
];

export const CHANGE_DELIVERY_LOCATION_PARAMS = [
  { var: '1', desc: 'Shipment ID', example: '"12345"' },
];

export function ParamsTable({ params, subtitle, maxHeight = 360 }) {
  const { t } = useTranslation('ui');
  return (
    <Box component="div" sx={{ maxWidth: 420, maxHeight, overflow: 'auto' }}>
      {subtitle && (
        <MuiTypography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          {subtitle}
        </MuiTypography>
      )}
      <Box
        component="table"
        sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}
      >
        <thead>
          <tr>
            <th style={thStyle}>{t('settingsPages.common.tableVariable')}</th>
            <th style={thStyle}>{t('settingsPages.common.tableDescription')}</th>
            <th style={thStyle}>{t('settingsPages.common.tableExample')}</th>
          </tr>
        </thead>
        <tbody>
          {params.map(({ var: v, desc, example }) => (
            <tr key={v}>
              <td style={{ ...tableCellStyle, fontFamily: 'monospace' }}>{v}</td>
              <td style={tableCellStyle}>{desc}</td>
              <td style={{ ...tableCellStyle, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                {example}
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

export const NO_PARAMS_MESSAGE = 'No variables. Use static body only in your Twilio template.';
